'use client';

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCaretRight,
  faCaretLeft,
  faCaretDown,
} from '@fortawesome/pro-solid-svg-icons';
import useDiscoveryList from 'src/hooks/discovery/useDiscoveryList';
import { LOCATION } from 'src/hooks/filters/constants';
import useIsAdvancedSearchActive from 'src/hooks/discovery/useIsAdvancedSearchActive';
import { useSelector } from 'react-redux';
import { selectDiscoveryMapCurrentTerritory } from 'src/redux/slices/discoveryMapSlice';
import { useSavedVolume } from 'src/hooks/savedVolume';
import { useGetDiscoveryListQuery } from 'src/api/redux';
import { useExecuteAdvancedSearchQuery } from 'src/api/redux/advancedSearches';
import { useDiscoveryListQueryParams } from '../../hooks/useDiscoveryQueryParams';
import { useAdvancedSearchQueryParams } from '../../hooks/useAdvancedSearchQueryParams';

const MAX_RESULTS = 500;

const Pagination = (): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    page: discoveryPage,
    setPage: setDiscoveryPage,
    pageSize,
  } = useDiscoveryList();
  const currentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);
  const { appliedSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);
  const isAdvancedSearchSelected = useIsAdvancedSearchActive();

  // Standard discovery query
  const { queryParams, skip } = useDiscoveryListQueryParams();
  const { data: queryData } = useGetDiscoveryListQuery(queryParams, {
    skip: skip || isAdvancedSearchSelected,
  });

  // Advanced search query
  const { queryParams: advSearchParams, skip: skipAdvSearch } =
    useAdvancedSearchQueryParams();
  const { data: advSearchData } = useExecuteAdvancedSearchQuery(
    advSearchParams,
    { skip: skipAdvSearch || !isAdvancedSearchSelected }
  );

  const discoveryResults = isAdvancedSearchSelected
    ? advSearchData
      ? { results: advSearchData.results, total_providers: advSearchData.count, count: advSearchData.count }
      : { results: [], total_providers: 0, count: 0 }
    : skip
      ? { results: [], total_providers: 0, count: 0 }
      : queryData;

  // Reset to page 1 when territory or code set changes
  useEffect(() => {
    setDiscoveryPage(1);
  }, [
    currentTerritory?.id,
    appliedSavedVolume?.id,
    appliedSavedVolume?.volumeType,
    setDiscoveryPage,
  ]);

  const page = discoveryPage;
  const totalCount =
    discoveryResults?.count ?? discoveryResults?.total_providers ?? 0;

  const setPage = setDiscoveryPage;

  const displayCount = Math.min(totalCount, MAX_RESULTS);
  const numberOfPages = Math.ceil(displayCount / pageSize);

  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, displayCount);

  const handlePageChange = (e: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setIsOpen(false);
  };

  return (
    <div className="self-stretch h-14 p-4 inline-flex justify-between items-center">
      <div className="flex justify-center items-center gap-1">
        <div className="text-right justify-start text-secondary/60 text-sm font-normal font-['Inter'] leading-tight">
          {displayCount > 0
            ? `Showing ${startIndex}-${endIndex} of ${
                totalCount > MAX_RESULTS ? `${MAX_RESULTS}` : totalCount
              }`
            : 'No results'}
        </div>
        {/* TODO: not even on the mocks anymore, commenting out for now. <Icon icon={faCircleQuestion} className="text-neutral-400 w-4 h-4" /> */}
      </div>
      <div className="flex justify-center items-center gap-2">
        <button
          onClick={() => page > 1 && handlePageChange(null, page - 1)}
          className="w-8 h-8 px-4 py-2 rounded-full flex justify-center items-center gap-2.5 cursor-pointer hover:bg-neutral-100 border border-neutral-200  disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={page === 1}
        >
          <Icon icon={faCaretLeft} className="text-neutral-600 w-4 h-4" />
        </button>
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="h-8 px-3 py-3.5 bg-common-White rounded-full flex justify-start items-center gap-2 cursor-pointer hover:bg-neutral-100 border border-neutral-200 "
          >
            <div className="justify-start text-primary text-sm font-normal font-['Inter'] leading-tight">
              Page {page}
            </div>
            <Icon
              icon={faCaretDown}
              className={`text-secondary w-2.5 h-1.5 transition-all duration-300 ${
                isOpen ? '-rotate-90' : 'rotate-0'
              }`}
            />
          </button>
          {isOpen && (
            <div className="absolute bottom-full mb-1 left-0 w-auto whitespace-nowrap bg-white rounded-lg shadow-lg border border-neutral-200 py-1 max-h-60 overflow-y-auto z-50">
              {Array.from({ length: numberOfPages }, (_, i) => (
                <div
                  key={i + 1}
                  onClick={(e) => handlePageChange(e, i + 1)}
                  className="px-3 py-2 hover:bg-neutral-100 cursor-pointer text-secondary text-sm font-normal font-['Inter']"
                >
                  Page {i + 1}
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() =>
            page < numberOfPages && handlePageChange(null, page + 1)
          }
          className="w-8 h-8 px-4 py-2 rounded-full flex justify-center items-center gap-2.5 cursor-pointer hover:bg-neutral-100 border border-neutral-200  disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={page === numberOfPages}
        >
          <Icon icon={faCaretRight} className="text-neutral-600 w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
